import React, { ChangeEvent, useCallback } from 'react';
import Prompt from 'src/app/components/Prompt/Prompt';
import { ROUTES } from 'src/app/constants';
import styles from './Footer.module.scss';
import SendButton from '../SendButton/SendButton';
import { t } from 'i18next';

type FooterPropType = {
  prompt?: string;
  conversationId?: number;
  onPromptInputChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onPromptSend?: (event?: string) => void;
  disablePrompt?: boolean;
  hiddenWithDisabledFocus?: boolean;
};

function Footer(props: FooterPropType) {
  const {
    prompt,
    conversationId,
    disablePrompt,
    onPromptInputChange,
    onPromptSend,
    hiddenWithDisabledFocus,
  } = props;

  // APAI-2455: the footer must stay focusable while a reply is loading. Making
  // it inert/aria-hidden evicted focus from the prompt input to <body>, which
  // makes screen readers restart reading from the top of the page.
  const triggerSend = useCallback(
    (event: React.FormEvent<HTMLFormElement> | ChangeEvent<EventTarget>) => {
      event.preventDefault();
      if (hiddenWithDisabledFocus || !prompt?.trim()) return;
      const eventType = event.type === 'submit' ? 'type' : (event.type as string);
      onPromptSend?.(eventType);
    },
    [onPromptSend, prompt, hiddenWithDisabledFocus]
  );

  return (
    <div className='footer-section'>
      <div className={'flex items-center mt-space-5 mb-space-4'}>
        <Prompt
          handlePromptInput={onPromptInputChange}
          promptValue={prompt}
          onSubmit={triggerSend}
          disabled={disablePrompt}
          readOnly={hiddenWithDisabledFocus}
        ></Prompt>
        <SendButton
          className='flex-none'
          onClickHandler={triggerSend}
          isDisabled={!prompt || hiddenWithDisabledFocus}
        />
      </div>
      <div className={styles.footerContainer}>
        <div className='mt-space-2 text-center'>
          <p className='text-gray6D text-body-small'>
            {t('INACCURATE_AI')}
            <a
              className='underline text-hyperlink hover:text-blue-800'
              target='_blank'
              rel='noopener noreferrer'
              href={ROUTES.EXTERNAL.FOOTER_FAQ}
              aria-label={t('FAQ_FULL')}
            >
              {t('FAQS')}
            </a>
          </p>
          {/** NOTE: only for debug purpose */}
          {conversationId && (
            <div className='font-inter-semiBold text-center text-xs'>
              {t('CONVERSATION_ID')}: {conversationId}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Footer;
